// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3).
// customInterceptor: all modes. VDRInterceptor: appends `projectId` when Mode3ContextService is
// populated (Mode 3), or `source=vdr` when the current route has that query param (Mode 2).
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, filter, map, Observable, throwError } from 'rxjs';
import { SOURCE } from '../common/constants/routes.constant';
import { IGeneralItem } from '../common/models/general.model';
import { Mode3ContextService } from '../services/mode3-context.service';

export interface IResponseError {
  errorMessage: string;
  isError: boolean;
}

// for response which is an array
export interface IResponseArray extends IResponseError {
  results: IResponseValue[];
  totalCount: number;
}

// for response which is an object
export interface IResponseValue extends IResponseError {
  value: IGeneralItem;
}

// VDR envelope shape (Mode 2 + Mode 3). Emitted by VDRApiResponse<T> on the backend.
interface IVdrEnvelope {
  status: 'Success' | 'Failure';
  code: number;
  message: string;
  data: unknown;
}

export const customInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    filter((event) => event.type === HttpEventType.Response),
    map((resp) => {
      const { isError, errorMessage, ...data } = resp.body as IResponseError;
      if (!isError && !errorMessage) {
        if ('results' in data) {
          // if data has 'results' property, meaning that body is type of IResponseArray
          // return value property in each result item
          const { results, ...rest } = data as IResponseArray;
          return resp.clone({
            body: {
              ...rest,
              results: results?.map(({ value }) => value) || [],
            },
          });
        } else if ('value' in data) {
          // if data has 'value' property, meaning that body is type of IResponseValue
          // return value property in body
          return resp.clone({ body: (data as IResponseValue).value });
        } else if ('status' in data && 'code' in data && 'data' in data) {
          // VDR envelope shape (VDRApiResponse<T> from the backend). Only hit by Mode 3 calls
          // and any future frontend caller of /vdr/*. Unwrap into the raw payload, or throw the
          // envelope's own message on a Failure so the observable surfaces a real error.
          const envelope = data as unknown as IVdrEnvelope;
          if (envelope.status === 'Failure') {
            throw new Error(envelope.message || 'VDR API request failed');
          }
          return resp.clone({ body: envelope.data });
        }
      }

      // in case response has error
      throw new Error(errorMessage);
    }),
    catchError((error: unknown) => {
      // HttpErrorResponse = a genuine transport/server failure (network, 4xx/5xx, auth). Plain
      // Error = our own synthetic throw above for a 200 OK "isError" API response, which most
      // callers already treat as a normal empty state (e.g. no solutions selected yet) — logging
      // those as hard console errors is just alarming noise, not a real problem.
      if (error instanceof HttpErrorResponse) {
        console.error(`Error in request to api ${req.url}:`, error);
      }
      return throwError(() => error);
    }),
  );
};

export const VDRInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const activeRoute = inject(ActivatedRoute);
  const mode3Context = inject(Mode3ContextService);
  const { queryParams } = activeRoute.snapshot;
  const isVDR = queryParams['source'] === SOURCE.VDR;
  // Mode 3 is identified purely by the presence of a sticky projectId in Mode3ContextService,
  // NOT by ?source=vdr. Keeps Mode 3 requests independent of Mode 2's legacy source-query flag.
  const mode3ProjectId = mode3Context.projectId();
  let modifyReq = req.clone();

  if (mode3ProjectId) {
    modifyReq = req.clone({ setParams: { projectId: mode3ProjectId } });
  } else if (isVDR) {
    modifyReq = req.clone({ setParams: { source: SOURCE.VDR } });
  }

  return next(modifyReq);
};
